import { 
  KashierConfig, 
  KashierPaymentRequest, 
  KashierPaymentResponse, 
  KashierWebhookPayload,
  PaymentSession,
  PaymentCallback,
  PaymentResult,
  RefundRequest,
  RefundResult,
  SubscriptionPaymentContext
} from '../types/payment';
import { Subscription } from '../types/subscription';
import { getCurrentMillis } from '../utils/dateUtils';
import { KASHIER_CONFIG } from '../config/kashier';

class KashierService {
  private config: KashierConfig;

  constructor() {
    this.config = {
      merchantId: KASHIER_CONFIG.MERCHANT_ID,
      apiKey: KASHIER_CONFIG.API_KEY,
      baseUrl: KASHIER_CONFIG.baseUrl,
      currency: KASHIER_CONFIG.CURRENCY as 'EGP',
      environment: KASHIER_CONFIG.ENVIRONMENT,
      webhookSecret: KASHIER_CONFIG.WEBHOOK_SECRET
    };
  }

  /**
   * Create a payment session for subscription
   */
  async createPaymentSession(
    subscription: Subscription, 
    user: any, 
    context: SubscriptionPaymentContext
  ): Promise<PaymentSession> {
    try {
      
      const orderId = this.generateOrderId(user.id);
      const orderReference = this.generateOrderReference();
      const merchantOrderId = subscription.id;
      
      
      const paymentRequest: KashierPaymentRequest = {
        amount: context.amount,
        currency: 'EGP',
        orderId,
        orderReference,
        merchantOrderId,
        subscriptionPlanId: subscription.planId,
        subscriptionPlanName: this.getPlanName(subscription.planId),
        subscriptionPlanDescription: this.getPlanDescription(subscription.planId),
        subscriptionPlanPrice: context.amount.toString(),
        subscriptionPlanDuration: context.billingCycle === 'monthly' ? '1' : '12',
        subscriptionPlanDurationType: context.billingCycle,
        userId: user.id,
        userName: user.displayName || user.email,
        userEmail: user.email,
        userPhone: user.phoneNumber || '',
        language: 'en',
        successUrl: `${window.location.origin}/payment/success?session_id={session_id}`,
        failureUrl: `${window.location.origin}/payment/failure?session_id={session_id}`,
        webhookUrl: `${window.location.origin}/api/webhooks/kashier`,
        customer: {
          firstName: user.firstName || 'N/A',
          lastName: user.lastName || 'N/A',
          email: user.email,
          phone: user.phoneNumber || 'N/A'
        },
        billingAddress: {
          street: user.address || 'N/A',
          city: user.city || 'N/A',
          country: user.country || 'EG'
        },
        metadata: {
          userId: user.id,
          subscriptionId: subscription.id,
          planId: subscription.planId
        }
      };

      const paymentUrl = await this.createPaymentUrl(paymentRequest);
      
      const session: PaymentSession = {
        id: orderId,
        userId: user.id,
        subscriptionPlanId: subscription.planId,
        amount: context.amount,
        currency: 'EGP',
        status: 'pending',
        createdAt: getCurrentMillis().toString(),
        expiresAt: (Date.now() + 30 * 60 * 1000).toString(), // 30 minutes
        paymentUrl
      };

      return session;

    } catch (error) {
      console.error('❌ KashierService: Error creating payment session:', error);
      throw new Error('Failed to create payment session');
    }
  }

  /**
   * Create payment URL for iFrame integration
   */
  private async createPaymentUrl(paymentRequest: KashierPaymentRequest): Promise<string> {
    try {
      
      // Generate order hash for authentication
      const orderHash = await this.generateOrderHash(paymentRequest);
      
      // Create Kashier payment data for JavaScript SDK
      // According to Kashier documentation, we need to use the JavaScript SDK approach
      const paymentData = {
        merchantId: this.config.merchantId,
        orderId: paymentRequest.orderId,
        amount: paymentRequest.amount,
        currency: paymentRequest.currency,
        hash: orderHash,
        mode: this.config.environment === 'live' ? 'live' : 'test',
        merchantRedirect: paymentRequest.successUrl,
        serverWebhook: paymentRequest.webhookUrl,
        failureRedirect: paymentRequest.failureUrl,
        description: paymentRequest.subscriptionPlanName,
        customer: {
          name: `${paymentRequest.customer.firstName} ${paymentRequest.customer.lastName}`,
          email: paymentRequest.customer.email,
          phone: paymentRequest.customer.phone
        },
        metaData: {
          subscriptionPlanId: paymentRequest.subscriptionPlanId,
          subscriptionPlanDescription: paymentRequest.subscriptionPlanDescription,
          subscriptionPlanDuration: paymentRequest.subscriptionPlanDuration,
          subscriptionPlanDurationType: paymentRequest.subscriptionPlanDurationType
        }
      };
      
      // Create a proper HTML page URL for the Kashier JavaScript SDK
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Kashier Payment</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body>
          <div id="kashier-payment-container" style="width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center;">
            <div id="kashier-payment-form"></div>
          </div>
          <script>
            // Override window.location methods to handle navigation properly
            let originalLocation = window.location;
            
            // Store original methods
            const originalAssign = window.location.assign;
            const originalReplace = window.location.replace;
            
                        // Override location.assign
                        window.location.assign = function(url) {
                          handleNavigation(url);
                        };
                        
                        // Override location.replace
                        window.location.replace = function(url) {
                          handleNavigation(url);
                        };
            
            // Override location.href setter - try multiple approaches
            try {
              const originalHref = Object.getOwnPropertyDescriptor(window.location, 'href');
              if (originalHref && originalHref.set) {
                Object.defineProperty(window.location, 'href', {
                  get: originalHref.get,
                  set: function(url) {
                    handleNavigation(url);
                  }
                });
              }
            } catch (hrefError) {
              // Alternative approach - override the location object itself
              try {
                Object.defineProperty(window, 'location', {
                  get: function() {
                    return {
                      href: originalLocation.href,
                      assign: function(url) {
                        handleNavigation(url);
                      },
                      replace: function(url) {
                        handleNavigation(url);
                      }
                    };
                  },
                  set: function(url) {
                    handleNavigation(url);
                  }
                });
              } catch (locationError) {
                // Fallback to message-based approach
              }
            }
            
            function handleNavigation(url) {
              // Parse URL parameters to extract payment result
              try {
                const urlObj = new URL(url);
                const paymentStatus = urlObj.searchParams.get('paymentStatus');
                const transactionId = urlObj.searchParams.get('transactionId');
                const orderId = urlObj.searchParams.get('orderId');
                const cardDataToken = urlObj.searchParams.get('cardDataToken');
                const maskedCard = urlObj.searchParams.get('maskedCard');
                const cardBrand = urlObj.searchParams.get('cardBrand');
                const amount = urlObj.searchParams.get('amount');
                const currency = urlObj.searchParams.get('currency');
                
                console.log('Payment result from URL:', {
                  paymentStatus,
                  transactionId,
                  orderId,
                  cardDataToken,
                  maskedCard,
                  cardBrand,
                  amount,
                  currency
                });
                
                // Send payment result to parent
                if (window.parent && window.parent !== window) {
                  window.parent.postMessage({
                    type: 'kashier_payment_result',
                    paymentStatus: paymentStatus,
                    transactionId: transactionId,
                    orderId: orderId,
                    cardDataToken: cardDataToken,
                    maskedCard: maskedCard,
                    cardBrand: cardBrand,
                    amount: amount,
                    currency: currency,
                    redirectUrl: url,
                    orderId: '${paymentRequest.orderId}',
                    merchantId: '${this.config.merchantId}'
                  }, '*');
                }
                
                // Prevent actual navigation
                return;
              } catch (error) {
                console.error('Error parsing payment URL:', error);
              }
              
              // Send message to parent instead of navigating
              if (window.parent && window.parent !== window) {
                window.parent.postMessage({
                  type: 'kashier_redirect',
                  redirectUrl: url,
                  orderId: '${paymentRequest.orderId}',
                  merchantId: '${this.config.merchantId}'
                }, '*');
              }
            }
          </script>
          <script
            id="kashier-iFrame"
            src="https://payments.kashier.io/kashier-checkout.js"
            data-amount="${paymentRequest.amount}"
            data-hash="${orderHash}"
            data-currency="${paymentRequest.currency}"
            data-orderId="${paymentRequest.orderId}"
            data-merchantId="${this.config.merchantId}"
            data-merchantRedirect="${paymentRequest.successUrl}"
            data-serverWebhook="${paymentRequest.webhookUrl}"
            data-mode="${this.config.environment === 'live' ? 'live' : 'test'}"
            data-description="${paymentRequest.subscriptionPlanName}"
            data-customer='${JSON.stringify(paymentData.customer)}'
            data-metaData='${JSON.stringify(paymentData.metaData)}'
            data-failureRedirect="${paymentRequest.failureUrl}"
            data-enable3DS="true"
            data-interactionSource="Ecommerce"
          ></script>
        </body>
        </html>
      `;
      
      // Create a blob URL for the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const kashierUrl = URL.createObjectURL(blob);
      
      console.log('✅ KashierService: Created Kashier payment data:', paymentData);
      console.log('🔧 KashierService: HTML content length:', htmlContent.length);
      console.log('🔧 KashierService: Hash:', orderHash);
      
      // Using the correct Kashier iFrame URL format
      console.log('🔄 KashierService: Using correct Kashier iFrame URL format');
      return kashierUrl;

    } catch (error) {
      console.error('❌ KashierService: Error creating payment URL:', error);
      throw error;
    }
  }

  /**
   * Create a mock payment URL for testing purposes
   */
  private createMockPaymentUrl(paymentRequest: KashierPaymentRequest): string {
    const mockParams = new URLSearchParams({
      merchantId: this.config.merchantId,
      orderId: paymentRequest.orderId,
      amount: paymentRequest.amount.toString(),
      currency: paymentRequest.currency,
      mode: this.config.environment === 'live' ? 'live' : 'test',
      customerName: `${paymentRequest.customer.firstName} ${paymentRequest.customer.lastName}`,
      customerEmail: paymentRequest.customer.email,
      customerPhone: paymentRequest.customer.phone,
      orderReference: paymentRequest.orderReference,
      // Add mock flag to identify this as a test
      mock: 'true'
    });
    
    // Return a local mock payment page
    return `${window.location.origin}/mock-payment?${mockParams.toString()}`;
  }

  /**
   * Generate order hash for payment authentication
   * According to Kashier docs: hash = HMAC-SHA256(/?payment=${mid}.${orderId}.${amount}.${currency}, secret)
   */
  private async generateOrderHash(paymentRequest: KashierPaymentRequest): Promise<string> {
    // Create the path string as per Kashier documentation
    const path = `/?payment=${this.config.merchantId}.${paymentRequest.orderId}.${paymentRequest.amount}.${paymentRequest.currency}`;
    console.log('🔧 Hash generation input:', {
      merchantId: this.config.merchantId,
      orderId: paymentRequest.orderId,
      amount: paymentRequest.amount,
      currency: paymentRequest.currency,
      path: path,
      apiKeyLength: this.config.apiKey.length
    });
    
    // Use HMAC SHA256 as per Kashier documentation
    const hash = await this.generateHMACHash(path, this.config.apiKey);
    console.log('🔧 Generated HMAC SHA256 hash:', hash);
    return hash;
  }

  /**
   * Generate HMAC SHA256 hash (for production use)
   * This is a browser-compatible implementation
   */
  private async generateHMACHash(data: string, key: string): Promise<string> {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(key);
    const dataBuffer = encoder.encode(data);
    
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, dataBuffer);
    const hashArray = Array.from(new Uint8Array(signature));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }


  /**
   * Verify webhook signature
   */
  verifyWebhookSignature(payload: any, signature: string): boolean {
    try {
      // In a real implementation, you would verify the signature using Kashier's webhook secret
      // This is a simplified version - you should implement proper signature verification
      const expectedSignature = this.generateSignature(payload);
      return signature === expectedSignature;
    } catch (error) {
      console.error('❌ KashierService: Error verifying webhook signature:', error);
      return false;
    }
  }

  /**
   * Process webhook callback
   */
  async processWebhookCallback(payload: KashierWebhookPayload): Promise<PaymentResult> {
    try {
      console.log('🔄 KashierService: Processing webhook callback:', payload);

      // Verify signature
      if (!this.verifyWebhookSignature(payload, payload.signature)) {
        throw new Error('Invalid webhook signature');
      }

      const result: PaymentResult = {
        success: payload.paymentStatus === 'SUCCESS',
        transactionId: payload.transactionId,
        orderId: payload.orderId,
        paymentStatus: payload.paymentStatus,
        message: payload.paymentStatus === 'SUCCESS' ? 'Payment successful' : 'Payment failed'
      };

      console.log('✅ KashierService: Webhook processed successfully:', result);
      return result;

    } catch (error) {
      console.error('❌ KashierService: Error processing webhook:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Process payment callback (for direct integration)
   */
  async processPaymentCallback(callback: PaymentCallback): Promise<PaymentResult> {
    try {
      console.log('🔄 KashierService: Processing payment callback:', callback);

      // In a real implementation, you would verify the callback with Kashier's API
      const result: PaymentResult = {
        success: callback.paymentStatus === 'SUCCESS',
        transactionId: callback.transactionId,
        orderId: callback.orderId,
        paymentStatus: callback.paymentStatus,
        message: callback.paymentStatus === 'SUCCESS' ? 'Payment successful' : 'Payment failed'
      };

      console.log('✅ KashierService: Payment callback processed:', result);
      return result;

    } catch (error) {
      console.error('❌ KashierService: Error processing payment callback:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Process refund
   */
  async processRefund(refundRequest: RefundRequest): Promise<RefundResult> {
    try {
      console.log('🔄 KashierService: Processing refund:', refundRequest);

      const response = await fetch(`${this.config.baseUrl}/v1/refunds`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`,
          'X-Merchant-ID': this.config.merchantId
        },
        body: JSON.stringify(refundRequest)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const result: RefundResult = {
        success: data.success,
        refundId: data.refundId,
        message: data.message
      };

      console.log('✅ KashierService: Refund processed:', result);
      return result;

    } catch (error) {
      console.error('❌ KashierService: Error processing refund:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Generate order ID in format: {user-id}_{currentTimeInMillis}
   */
  private generateOrderId(userId: string): string {
    return `${userId}_${Date.now()}`;
  }

  /**
   * Generate order reference
   */
  private generateOrderReference(): string {
    return `MORAFAA-${Date.now()}`;
  }

  /**
   * Generate signature for webhook verification
   */
  private generateSignature(payload: any): string {
    // In a real implementation, you would use Kashier's signature generation method
    // This is a simplified version using browser-compatible crypto
    const data = JSON.stringify(payload);
    return this.simpleHash(data + this.config.webhookSecret);
  }

  /**
   * Simple hash function for browser compatibility
   */
  private simpleHash(str: string): string {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Get plan name by ID
   */
  private getPlanName(planId: string): string {
    const planNames: { [key: string]: string } = {
      'free': 'Free Plan',
      'basic': 'Basic Plan',
      'professional': 'Professional Plan',
      'enterprise': 'Enterprise Plan'
    };
    return planNames[planId] || 'Unknown Plan';
  }

  /**
   * Get plan description by ID
   */
  private getPlanDescription(planId: string): string {
    const descriptions: { [key: string]: string } = {
      'free': 'Basic features for getting started',
      'basic': 'Essential features for small practices',
      'professional': 'Advanced features for growing practices',
      'enterprise': 'Full features for large organizations'
    };
    return descriptions[planId] || 'Subscription plan';
  }

  /**
   * Get iFrame URL for embedded payment
   */
  getIframeUrl(sessionId: string): string {
    return `${this.config.baseUrl}/payment/iframe?session_id=${sessionId}&merchant_id=${this.config.merchantId}`;
  }

  /**
   * Get payment status
   */
  async getPaymentStatus(transactionId: string): Promise<PaymentResult> {
    try {
      const response = await fetch(`${this.config.baseUrl}/v1/payments/${transactionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'X-Merchant-ID': this.config.merchantId
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: data.paymentStatus === 'SUCCESS',
        transactionId: data.transactionId,
        orderId: data.orderId,
        paymentStatus: data.paymentStatus,
        message: data.message
      };

    } catch (error) {
      console.error('❌ KashierService: Error getting payment status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

export const kashierService = new KashierService();
